<template>
	<div>
		<div class="flex xs-12 xs-between mt6">
			<div class="fs4 lh125 fw7 c1 mb2">{{ $t("credentials") }}</div>

			<j-button
				v-if="hasActions"
				v-can:or="[
					'writeIndividuals',
					'writeCompanies',
					'writeObjects',
					'writeInternals'
				]"
				type="tertiary"
				@click="handleUpdate"
			>
				<crm-icon name="edit" color="c2" size="small" />
				{{ $t("update") }}
			</j-button>
		</div>
		<j-card v-if="!inlineUpdate" class="bg5">
			<j-list :list="computedCredentials">
				<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
					<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">
						{{ item.label }}
					</div>
					<div class="flex xs-6 xs-left xs-middle lh125">{{ item.value }}</div>
				</div>
			</j-list>
			<div class="bg5 ta2 fs3 p2" v-if="!computedCredentials">
				{{ $t("errors.no_data") }}
			</div>
		</j-card>

		<!-- Update Modal -->
		<core-modal
			:name="`updateCredential${id}`"
			:headline="$t('update_credentials')"
		>
			<entity-update
				:id="id"
				:for="typename"
				:config="updateConfig"
				@updated="afterUpdated"
			/>
		</core-modal>

		<!-- Inline Update -->
		<div v-if="inlineUpdate" class="bg4 r2 bc7 b1">
			<div class="flex xs-between p2 xs-12">
				<div class="fw7 fs3 c1">{{ $t("update_credentials") }}</div>
				<crm-icon
					size="small"
					color="c1"
					name="cross"
					class="cu2"
					@click.native="inlineUpdate = false"
				/>
			</div>
			<entity-update
				:id="id"
				size="small"
				fsLabel="fs3"
				:for="typename"
				:config="updateConfig"
				@updated="afterUpdated"
			/>
		</div>
	</div>
</template>

<script>
import JCard from "@/components/JCard/"
import JList from "@/components/JList/"
import JButton from "@/components/JButton/"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import EntityUpdate from "./EntityUpdate"
import {
	countries,
	salutationTypes,
	genderTypes,
	maritalStatusTypes
} from "@/forms/enums.js"
import { firstLetterUppercase } from "@/utils/"
import dayjs from "dayjs"

export default {
	props: {
		id: String,
		typename: String,
		payload: Object,
		hasActions: Boolean,
		mode: String
	},

	components: {
		JCard,
		JList,
		CrmIcon,
		JButton,
		CoreModal,
		EntityUpdate
	},

	data() {
		return {
			inlineUpdate: false,
			updateConfig: null
		}
	},

	methods: {
		handleUpdate() {
			if (this.mode === "modal") this.$modal.show(`updateCredential${this.id}`)
			if (this.mode === "inline") this.inlineUpdate = !this.inlineUpdate

			if (this.typename === "individual") this.updateIndividual()
			if (this.typename === "company") this.updateCompany()
			if (this.typename === "object") this.updateObject()
			if (this.typename === "internal") this.updateInternal()
			if (this.typename === "organization") this.updateOrganization()
		},

		updateIndividual() {
			const {
				name,
				gender,
				salutation,
				englishFirstName,
				englishLastName,
				chineseFirstName,
				chineseLastName,
				dateOfBirth,
				maritalStatus,
				occupation,
				countryOfResidency,
				preferredCommunicationChannel,
				preferredLanguage
			} = this.payload

			this.updateConfig = this.updateConfig = [
				{
					componentName: "j-base-input-text",
					id: "name",
					label: this.$t("name"),
					value: name
				},
				{
					componentName: "j-base-radios",
					id: "gender",
					label: this.$t("gender"),
					options: genderTypes,
					value: gender
				},
				{
					componentName: "j-base-radios",
					id: "salutation",
					label: this.$t("salutation"),
					options: salutationTypes,
					value: salutation
				},
				{
					componentName: "j-base-input-text",
					id: "englishFirstName",
					label: this.$t("english_first_name"),
					value: englishFirstName
				},
				{
					componentName: "j-base-input-text",
					id: "englishLastName",
					label: this.$t("english_last_name"),
					value: englishLastName
				},
				{
					componentName: "j-base-input-text",
					id: "chineseFirstName",
					label: this.$t("chinese_first_name"),
					value: chineseFirstName
				},
				{
					componentName: "j-base-input-text",
					id: "chineseLastName",
					label: this.$t("chinese_last_name"),
					value: chineseLastName
				},
				{
					componentName: "j-base-date-picker-simple",
					id: "dateOfBirth",
					label: this.$t("date_of_birth"),
					value: dateOfBirth ? dayjs(dateOfBirth).format("DD/MM/YYYY") : null
				},
				{
					componentName: "j-base-radios",
					id: "maritalStatus",
					label: this.$t("marital_status"),
					options: maritalStatusTypes,
					value: maritalStatus
				},
				{
					componentName: "j-base-input-text",
					id: "occupation",
					label: this.$t("occupation"),
					value: occupation
				},
				{
					componentName: "j-base-select",
					id: "countryOfResidency",
					label: this.$t("country_of_residency"),
					options: countries,
					value: countryOfResidency
				},
				{
					componentName: "j-base-input-text",
					id: "preferredCommunicationChannel",
					label: this.$t("preferred_communication_channel"),
					value: preferredCommunicationChannel
				},
				{
					componentName: "j-base-input-text",
					id: "preferredLanguage",
					label: this.$t("preferred_language"),
					value: preferredLanguage
				}
			]
		},

		updateCompany() {
			const { name, natureOfBusiness, registrationNumber } = this.payload

			this.updateConfig = [
				{
					componentName: "j-base-input-text",
					id: "nameFormat",
					label: this.$t("name"),
					value: name
				},
				{
					componentName: "j-base-input-text",
					id: "natureOfBusiness",
					label: this.$t("nature_of_business"),
					value: natureOfBusiness
				},
				{
					componentName: "j-base-input-text",
					id: "registrationNumber",
					label: this.$t("registration_number"),
					value: registrationNumber
				}
			]
		},

		updateObject() {
			const { name } = this.payload

			this.updateConfig = [
				{
					componentName: "j-base-input-text",
					id: "nameFormat",
					label: this.$t("name"),
					value: name
				}
			]
		},

		updateInternal() {
			const {
				englishFirstName,
				englishLastName,
				chineseFirstName,
				chineseLastName,
				internalCode,
				isActive
			} = this.payload

			this.updateConfig = [
				{
					componentName: "j-base-input-text",
					id: "englishFirstName",
					label: this.$t("english_first_name"),
					value: englishFirstName
				},
				{
					componentName: "j-base-input-text",
					id: "englishLastName",
					label: this.$t("english_last_name"),
					value: englishLastName
				},
				{
					componentName: "j-base-input-text",
					id: "chineseFirstName",
					label: this.$t("chinese_first_name"),
					value: chineseFirstName
				},
				{
					componentName: "j-base-input-text",
					id: "chineseLastName",
					label: this.$t("chinese_last_name"),
					value: chineseLastName
				},
				{
					componentName: "j-base-toggle",
					id: "isActive",
					label: this.$t("active_referral"),
					value: isActive
				},
				{
					componentName: "j-base-input-text",
					id: "internalCode",
					label: this.$t("code"),
					value: internalCode
				}
			]
		},

		updateOrganization() {
			const { name, source, isActive } = this.payload

			this.updateConfig = [
				{
					componentName: "j-base-input-text",
					id: "name",
					label: this.$t("name"),
					value: name
				},

				{
					componentName: "j-base-toggle",
					id: "isActive",
					label: this.$t("active_referral"),
					value: isActive
				},
				{
					componentName: "j-base-input-text",
					id: "source",
					label: this.$t("source"),
					value: source
				}
			]
		},

		afterUpdated() {
			if (this.mode === "modal") this.$modal.hide(`updateCredential${this.id}`)
			if (this.mode === "inline") this.inlineUpdate = false
			this.$emit("updated")
		}
	},

	computed: {
		computedCredentials() {
			if (this.typename === "individual")
				return [
					{
						label: this.$t("name"),
						value: this.payload.name
					},
					{
						label: this.$t("gender"),
						value: this.payload.gender
							? firstLetterUppercase(this.payload.gender)
							: null
					},
					{
						label: this.$t("salutation"),
						value: this.payload.salutation
							? firstLetterUppercase(this.payload.salutation)
							: null
					},
					{
						label: this.$t("english_first_name"),
						value: this.payload.englishFirstName
					},
					{
						label: this.$t("english_last_name"),
						value: this.payload.englishLastName
					},
					{
						label: this.$t("chinese_first_name"),
						value: this.payload.chineseFirstName
					},
					{
						label: this.$t("chinese_last_name"),
						value: this.payload.chineseLastName
					},
					{
						label: this.$t("date_of_birth"),
						value: this.payload.dateOfBirth
							? dayjs(this.payload.dateOfBirth).format("DD/MM/YYYY")
							: null
					},
					{
						label: this.$t("marital_status"),
						value: this.payload.maritalStatus
							? firstLetterUppercase(this.payload.maritalStatus)
							: null
					},
					{
						label: this.$t("occupation"),
						value: this.payload.occupation
					},
					{
						label: this.$t("country_of_residency"),
						value: this.payload.countryOfResidency
							? this.$t(`countries.${this.payload.countryOfResidency}`)
							: null
					},
					{
						label: this.$t("accepts_marketing"),
						value: this.payload.acceptsMarketing
							? this.$t("yes")
							: this.$t("no")
					},
					{
						label: this.$t("preferred_communication_channel"),
						value: this.payload.preferredCommunicationChannel
					},
					{
						label: this.$t("preferred_language"),
						value: this.payload.preferredLanguage
					}
				]

			if (this.typename === "company")
				return [
					{
						label: this.$t("name"),
						value: this.payload.name
					},
					{
						label: this.$t("nature_of_business"),
						value: this.payload.natureOfBusiness
					},
					{
						label: this.$t("registration_number"),
						value: this.payload.registrationNumber
					}
				]

			if (this.typename === "object")
				return [
					{
						label: this.$t("name"),
						value: this.payload.name
					}
				]

			if (this.typename === "internal")
				return [
					{
						label: this.$t("english_first_name"),
						value: this.payload.englishFirstName
					},
					{
						label: this.$t("english_last_name"),
						value: this.payload.englishLastName
					},
					{
						label: this.$t("chinese_first_name"),
						value: this.payload.chineseFirstName
					},
					{
						label: this.$t("chinese_last_name"),
						value: this.payload.chineseLastName
					},
					{
						label: this.$t("code"),
						value: this.payload.internalCode
					},
					{
						label: this.$t("active_referral"),
						value: this.payload.isActive ? this.$t("yes") : this.$t("no")
					}
				]

			if (this.typename === "organization")
				return [
					{
						label: this.$t("name"),
						value: this.payload.name
					},
					{
						label: this.$t("code"),
						value: this.payload.internalCode
					},
					{
						label: this.$t("source"),
						value: this.payload.source
					},
					{
						label: this.$t("active_referral"),
						value: this.payload.isActive ? this.$t("yes") : this.$t("no")
					}
				]

			return []
		}
	}
}
</script>
